import {useCallback, useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';

import {getOptionsGroupingUIProcessed} from '@amblique/king-living/app/utils/product-utils';

const customizeStepTitleMessages = defineMessages({
  chooseYourRecommendationType: {
    defaultMessage: 'Choose your {label}',
    id: 'customize_panel.step.choose_your_recommendation_type.title',
  },
  cover: {
    defaultMessage: 'Choose your cover',
    id: 'customize_panel.step.cover.title',
  },
  extraOptions: {
    defaultMessage: 'Choose your options',
    id: 'customize_panel.step.extra.options.title',
  },
});

/**
 * Build the ordered list of customize steps from a product.
 * Returns cover step first, then one step per option container.
 *
 * Layout decision is identical for Complex and Simple PDP (KINDW-1124):
 * a single-value container resolves to radio layout (then swatch-vs-radio inside
 * <OptionStepRadio> per product data), and a multi-value container resolves to
 * slot-cards (per TSD - PDP Simple §"Configurable Options" → "Mostly same as
 * TSD - PDP Complex#Configurable-options").
 *
 * The `isSimplePDP` option is accepted for forward compatibility but is no
 * longer used by buildSteps itself — kept on the signature so call sites that
 * still pass it (Simple PDP `<CustomizeStepsInline>`) don't need a churn change.
 *
 * @param {Object} product - SFCC product with c_optionsGroupingUIProcessed
 * @param {Object} [options] - Build options
 * @param {boolean} [options.isSimplePDP=false] - Reserved for future use; ignored.
 * @returns {Array} Array of step descriptor objects
 */

export function buildSteps(product, {isSimplePDP = false} = {}) {
  const optionContainers = getOptionsGroupingUIProcessed(product) || [];

  const coverStep = {
    hasData: (groups) => {
      if (!groups || groups.length === 0) return false;

      // Check if at least one group has at least one orderable variant
      return groups.some((group) => {
        // Check byColor variants
        const hasOrderableInColor = group.byColor?.some((colorGroup) => colorGroup.variants?.some((v) => v.orderable === true));

        if (hasOrderableInColor) return true;

        // Check byCollection variants
        const hasOrderableInCollection = group.byCollection?.some((collectionGroup) => collectionGroup.variants?.some((v) => v.orderable === true));

        return hasOrderableInCollection;
      });
    },
    id: 'cover',
    layoutType: 'cover',
    titleKey: 'customize_panel.step.cover.title',
  };

  const optionSteps = optionContainers.map((container) => ({
    container,
    customTitle: container.containerName,
    hasData: () => {
      if (!container.values || container.values.length === 0) return false;

      return container.values.some((value) => {
        if (!Array.isArray(value.products) || value.products.length === 0) return false;
        if (value.isVisible !== false) return true;

        return value.isRequired === true;
      });
    },
    id: container.containerId,
    // Layout decision applies to BOTH Complex and Simple PDP (KINDW-1124 / TSD §"Configurable Options"):
    //   - Single recommendationTypeId  → radio layout. <OptionStepRadio> then makes the final
    //     swatch-vs-radio decision once recommendation product data is loaded (swatch: all rec
    //     extra price 0/null; radio: any extra price > 0; not shortDescription).
    //   - Multiple recommendationTypeIds → slot-cards. Each slot renders as a "Choose" placeholder
    //     row that opens the PDP-07 product picker sub-view, exactly like Complex PDP. Per TSD
    //     "Mostly same as TSD - PDP Complex#Configurable-options" + FSD §SECTION-PDP-SIMPLE-08.2:
    //     "Clicking on 'Choose' will bring up the flyout as detailed in PDP-07: Customise Your Own".
    layoutType: container.values.length === 1 ? 'radio' : 'slot-cards',
  }));

  return [coverStep, ...optionSteps];
}

/**
 * Steps actually rendered in the customize UI.
 * Excludes:
 * - Option containers with isVisible === false
 * - Option containers with empty values array (e.g. extra container with only optional add-ons)
 *
 * Used for panel header "current / total" so totals match what the user sees (Point 29).
 *
 * @param {Array} steps - From buildSteps()
 * @returns {Array}
 */
export function getVisibleCustomizeSteps(steps) {
  if (!steps?.length) return [];

  return steps.filter((s) => {
    // Exclude steps with isVisible === false
    if (s.container && s.container.isVisible === false) return false;

    // Exclude steps with empty values array (no products to select)
    // This handles cases like "extra" container where all values are optional add-ons
    // and have been moved to optionalAddOns array, leaving values empty
    if (s.container && s.container.values && s.container.values.length === 0) return false;

    return true;
  });
}

export function findFirstIncompleteVisibleStep(steps, isStepComplete) {
  return getVisibleCustomizeSteps(steps).find((step) => !isStepComplete(step.id)) ?? null;
}

/**
 * 1-based index of a step among visible customize steps only.
 *
 * @param {string} stepId
 * @param {Array} steps - Full list from buildSteps()
 * @returns {number} 1..N, or 1 if stepId not found among visible (defensive)
 */
export function getVisibleStepDisplayIndex(stepId, steps) {
  const visible = getVisibleCustomizeSteps(steps);
  const idx = visible.findIndex((s) => s.id === stepId);

  return idx >= 0 ? idx + 1 : 1;
}

/** BEM / theme tokens derived from OGU layout — not container id or title. */
export const CUSTOMIZE_STEP_LAYOUT_KIND = {
  COVER: 'cover',
  MULTI_TYPE: 'multi-type',
  SINGLE_TYPE: 'single-type',
};

/**
 * True when the step uses slot-cards layout (container has multiple recommendation types).
 *
 * @param {Object} [step] - Step from buildSteps()
 * @returns {boolean}
 */
export function isMultiSlotCustomizeStep(step) {
  return step?.layoutType === 'slot-cards';
}

/**
 * True when the step uses inline radio layout (single recommendation type in container).
 *
 * @param {Object} [step] - Step from buildSteps()
 * @returns {boolean}
 */
export function isSingleTypeCustomizeStep(step) {
  return step?.layoutType === 'radio';
}

const CHOOSE_YOUR_CUSHIONS_NAME_RE = /choose\s+your\s+cushions?\b/i;

/**
 * Cushion step only: radio row uses bold name1 line + optional description below (not name1/name2 pair).
 *
 * @param {Object} [step] - Step from buildSteps() (not cover)
 * @returns {boolean}
 */
export function isCushionSingleTitleCustomizeStep(step) {
  if (!step || step.id === 'cover') return false;

  const idNorm = String(step.id || '').trim().toLowerCase();

  if (idNorm === 'cushions' || idNorm === 'cushion') return true;

  const containerIdNorm = String(step.container?.containerId || '').trim().toLowerCase();

  if (containerIdNorm === 'cushions' || containerIdNorm === 'cushion') return true;

  const titleBlob = [step.customTitle, step.container?.containerName]
    .filter(Boolean)
    .map((s) => String(s))
    .join(' ');

  return CHOOSE_YOUR_CUSHIONS_NAME_RE.test(titleBlob);
}

/** BM recommendationType display name: "Base", or "7 (Base)" → "Base". */
function resolveRecommendationTypeDisplayName(typeName) {
  if (typeName == null) return null;

  const trimmed = String(typeName).trim();

  if (!trimmed) return null;

  const wrapped = trimmed.match(/^\d+\s*\((.+)\)$/);

  if (wrapped) {
    const inner = wrapped[1].trim();

    return inner || trimmed;
  }

  return trimmed;
}

/**
 * True when a container/step id is the extra-options block (DE-228).
 * Catalog uses `extra` or `extra options` — do not match `exterior`.
 *
 * @param {string|null|undefined} id
 * @returns {boolean}
 */
export function isExtraContainerId(id) {
  const n = String(id || '').trim().toLowerCase();

  if (!n) return false;

  if (n === 'extra' || n === 'extra options') return true;

  return n.replace(/[\s_-]+/g, '') === 'extraoptions';
}

/**
 * True when this customize step is the extra-options container (DE-228).
 *
 * @param {Object} [step]
 * @returns {boolean}
 */
export function isExtraCustomizeStep(step) {
  if (!step || step.id === 'cover') return false;

  return isExtraContainerId(step.id) || isExtraContainerId(step.container?.containerId);
}

/** Option heading from BM recommendation type — not OGU containerId / containerName. */
function resolveOptionCustomizeStepTitle(step, intl) {
  if (!step) return null;

  const values = Array.isArray(step.container?.values) ? step.container.values : [];
  const visibleValues = values.filter((value) => value?.isVisible !== false);

  if (visibleValues.length > 1) {
    return intl.formatMessage(customizeStepTitleMessages.extraOptions);
  }

  if (visibleValues.length !== 1) return null;

  const typeLabel = resolveRecommendationTypeDisplayName(visibleValues[0]?.typeName);

  if (!typeLabel) return null;

  return intl.formatMessage(customizeStepTitleMessages.chooseYourRecommendationType, {
    label: typeLabel.toLowerCase(),
  });
}

/**
 * Get the display title for a step.
 *
 * @param {Object} step - Step descriptor
 * @param {Object} intl - react-intl intl object
 * @returns {string} Localised step title
 */
export function getStepTitle(step, intl) {
  if (step?.id === 'cover') {
    return intl.formatMessage(customizeStepTitleMessages.cover);
  }

  return resolveOptionCustomizeStepTitle(step, intl) ?? step.id;
}

/**
 * Stable token for BEM modifier / data attributes: `customize-steps-*--{kind}`.
 * Derived from step layout only — cover | single-type | multi-type.
 *
 * @param {Object} [step] - Step from buildSteps() (cover or option step)
 * @returns {string} `cover` | `single-type` | `multi-type` | `other`
 */
export function getCustomizeStepSlotKind(step) {
  if (!step) {
    return 'other';
  }

  if (step.id === 'cover' || step.layoutType === 'cover') {
    return CUSTOMIZE_STEP_LAYOUT_KIND.COVER;
  }

  if (step.layoutType === 'slot-cards') {
    return CUSTOMIZE_STEP_LAYOUT_KIND.MULTI_TYPE;
  }

  if (step.layoutType === 'radio') {
    return CUSTOMIZE_STEP_LAYOUT_KIND.SINGLE_TYPE;
  }

  return 'other';
}

/**
 * Determine whether a step is complete given current selections.
 *
 * A required slot is complete when that index has a `productId` (visible pick or
 * hidden+required preselect). Hidden optional slots do not block Done.
 *
 * @param {string} stepId - Step ID
 * @param {Object} selections - Current selections map
 * @param {Array} steps - Full steps array (needed to find container info)
 * @returns {boolean}
 */
export function isStepCompleteUtil(stepId, selections, steps) {
  if (stepId === 'cover') {
    return selections?.cover != null;
  }

  const step = steps.find((s) => s.id === stepId);

  if (!step) {
    return false;
  }

  if (typeof step.hasData === 'function' && !step.hasData()) {
    return true;
  }

  if (!step.container) {
    return false;
  }

  const values = step.container.values ?? [];

  if (values.length === 0) {
    return true;
  }

  const hasRequiredSlots = values.some((v) => v.isRequired);

  if (!hasRequiredSlots) {
    return true;
  }

  const stepSelections = selections?.[stepId];

  if (!stepSelections || !Array.isArray(stepSelections)) {
    return false;
  }

  return values.every((value, idx) => {
    if (!value.isRequired) {
      return true;
    }

    return Boolean(stepSelections[idx]?.productId);
  });
}

/**
 * Whether all customize steps are satisfied for ATC.
 *
 * The cover is a required selection whenever the product actually has covers to choose. Pass
 * `coverGroups` so the step is skipped only for products with no orderable cover variants; omit it
 * (Complex PDP) and the cover is always required. A Simple PDP renders its cover picker as the
 * swatch grid rather than an inline step card, but the selection is required just the same —
 * conflating "hidden from the step list" with "optional" was the DE-813 defect.
 *
 * @param {Object|null|undefined} selections
 * @param {Array} steps
 * @param {{ coverGroups?: unknown }} [options]
 * @returns {boolean}
 */
export function isCustomizeJourneyComplete(selections, steps, options = {}) {
  const {coverGroups} = options;

  if (!selections) return false;

  return steps.every((step) => {
    if (step.id === 'cover' && coverGroups != null && typeof step.hasData === 'function') {
      if (!step.hasData(coverGroups)) {
        return true;
      }
    }

    return isStepCompleteUtil(step.id, selections, steps);
  });
}

/**
 * Determine whether a step is locked.
 * When enableLocking is false (inline mode), all steps are always unlocked.
 * When enableLocking is true (flyout), a step is locked if any previous **visible**
 * wizard step is incomplete. Hidden OGU containers do not lock later UI steps.
 */
export function isStepLockedUtil(stepIndex, steps, selections, enableLocking) {
  if (!enableLocking) return false;
  if (stepIndex === 0) return false;

  const current = steps[stepIndex];
  const visible = getVisibleCustomizeSteps(steps);
  const visibleIndex = visible.findIndex((step) => step.id === current?.id);

  if (visibleIndex <= 0) return false;

  return visible.slice(0, visibleIndex).some((step) => !isStepCompleteUtil(step.id, selections, steps));
}

function hasCustomizeRequiredStepRegressed(steps, isStepComplete, stepsEverCompleteIds = []) {
  return steps.some(
    (step) => stepsEverCompleteIds.includes(step.id) && !isStepComplete(step.id)
  );
}

/** Customize flyout outer footer Next — disabled on scroll or when a step regressed this session. */
export function isCustomizeOuterNextDisabled({
  steps,
  isStepComplete,
  activeStepId = null,
  stepsEverCompleteIds = [],
}) {
  const visibleSteps = getVisibleCustomizeSteps(steps);

  if (!findFirstIncompleteVisibleStep(steps, isStepComplete)) {
    return false;
  }

  return (
    hasCustomizeRequiredStepRegressed(visibleSteps, isStepComplete, stepsEverCompleteIds) ||
    (activeStepId != null && !isStepComplete(activeStepId))
  );
}

/**
 * Hook that provides step building and completion/locking utilities for the customize journey.
 *
 * @param {Object} product - SFCC product
 * @param {Object} selections - Current selections map
 * @param {Array} coverGroups - Cover groups derived from product
 * @param {boolean} [enableLocking=false] - Whether step locking is active (flyout mode)
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.isSimplePDP=false] - Whether this is a simple PDP (affects layout type resolution)
 * @returns {{ steps, isStepComplete, isStepLocked, getStepTitle }}
 */
export function useCustomizeSteps(product, selections, coverGroups, enableLocking = false, options = {}) {
  const intl = useIntl();
  const {isSimplePDP = false} = options;

  const steps = useMemo(
    () => buildSteps(product, {isSimplePDP}),
    [product, isSimplePDP]
  );

  const isStepComplete = useCallback(
    (stepId) => isStepCompleteUtil(stepId, selections, steps),
    [selections, steps]
  );

  const isStepLocked = useCallback(
    (stepIndex) => isStepLockedUtil(stepIndex, steps, selections, enableLocking),
    [steps, selections, enableLocking]
  );

  const getTitle = useCallback((step) => getStepTitle(step, intl), [intl]);

  const hasCoverData = coverGroups && coverGroups.length > 0;

  return {
    steps,
    hasCoverData,
    isStepComplete,
    isStepLocked,
    getStepTitle: getTitle,
  };
}
